import { rootApi } from '../rootApi';
import {
  ChatRequest,
  ChatResponse,
  MatchResponse,
  StaleSettings,
  AiBudgetStatus,
  AiRestrictions,
  NudgesResponse,
  OutreachRequest,
  OrgPromptRequest,
  OutreachResponse,
  OrgPromptResponse,
} from './ai.types';

export const aiApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    // The job's saved ranking (JT1-1815). Fetched on every open, which costs nothing once the job
    // has been ranked, so a colleague's re-rank shows up.
    getAiMatch: builder.query<MatchResponse, { jobId: string; limit?: number }>({
      query: ({ jobId, limit = 10 }) => ({
        url: '/ai/match',
        params: { jobId, limit },
      }),
      providesTags: (_result, _error, { jobId }) => [{ type: 'AiMatch', id: jobId }],
    }),

    rerankAiMatch: builder.mutation<MatchResponse, { jobId: string; limit?: number }>({
      query: ({ jobId, limit = 10 }) => ({
        url: '/ai/match/rerank',
        method: 'POST',
        data: { jobId, limit },
      }),
      invalidatesTags: (_result, _error, { jobId }) => [{ type: 'AiMatch', id: jobId }],
    }),

    postAiChat: builder.mutation<ChatResponse, ChatRequest>({
      query: (body) => ({
        url: '/ai/chat',
        method: 'POST',
        data: body,
      }),
    }),

    getAiNudges: builder.query<NudgesResponse, void>({
      query: () => ({ url: '/ai/nudges' }),
      providesTags: ['Nudges'],
    }),

    postAiOutreach: builder.mutation<OutreachResponse, OutreachRequest>({
      query: (body) => ({
        url: '/ai/outreach',
        method: 'POST',
        data: body,
      }),
    }),

    getOrgPrompt: builder.query<OrgPromptResponse, void>({
      query: () => ({ url: '/ai/org-prompt' }),
      providesTags: ['OrgPrompt'],
    }),

    putOrgPrompt: builder.mutation<{ success: boolean }, OrgPromptRequest>({
      query: (body) => ({
        url: '/ai/org-prompt',
        method: 'PUT',
        data: body,
      }),
      invalidatesTags: ['OrgPrompt'],
    }),

    getMeetingPrompt: builder.query<OrgPromptResponse, void>({
      query: () => ({ url: '/ai/meeting-prompt' }),
      providesTags: ['MeetingPrompt'],
    }),

    putMeetingPrompt: builder.mutation<{ success: boolean }, OrgPromptRequest>({
      query: (body) => ({
        url: '/ai/meeting-prompt',
        method: 'PUT',
        data: body,
      }),
      invalidatesTags: ['MeetingPrompt'],
    }),

    getStaleSettings: builder.query<StaleSettings, void>({
      query: () => ({ url: '/ai/stale-settings' }),
      providesTags: ['StaleSettings'],
    }),

    putStaleSettings: builder.mutation<StaleSettings, Partial<StaleSettings>>({
      query: (body) => ({
        url: '/ai/stale-settings',
        method: 'PUT',
        data: body,
      }),
      invalidatesTags: ['StaleSettings', 'Nudges'],
    }),

    getAiRestrictions: builder.query<AiRestrictions, void>({
      query: () => ({ url: '/ai/restrictions' }),
      providesTags: ['AiRestrictions'],
    }),

    putAiRestrictions: builder.mutation<AiRestrictions, AiRestrictions>({
      query: (body) => ({
        url: '/ai/restrictions',
        method: 'PUT',
        data: body,
      }),
      // A rules change hides saved rankings until someone re-ranks (JT1-1815).
      invalidatesTags: ['AiRestrictions', 'AiMatch'],
    }),

    getAiBudgetStatus: builder.query<AiBudgetStatus, void>({
      query: () => ({ url: '/ai/budget-status' }),
      providesTags: ['AiBudgetStatus'],
    }),
  }),
});

export const {
  useGetAiMatchQuery,
  useRerankAiMatchMutation,
  usePostAiChatMutation,
  useGetAiNudgesQuery,
  usePostAiOutreachMutation,
  useGetOrgPromptQuery,
  usePutOrgPromptMutation,
  useGetMeetingPromptQuery,
  usePutMeetingPromptMutation,
  useGetStaleSettingsQuery,
  usePutStaleSettingsMutation,
  useGetAiRestrictionsQuery,
  usePutAiRestrictionsMutation,
  useGetAiBudgetStatusQuery,
} = aiApi;
